import { doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";

const DEFAULT_TTL_MS = 10 * 60 * 1000;

export function createUsernameCache({ loadUsername, ttlMs = DEFAULT_TTL_MS, now = () => Date.now() }) {
  const values = new Map();
  const inFlight = new Map();
  const listeners = new Set();

  const normalizeUid = (uid) => String(uid || "").trim();
  const normalizeName = (name) => String(name || "").trim();

  const notify = (uid, username) => {
    listeners.forEach((listener) => listener(uid, username));
  };

  const seed = (uidValue, usernameValue) => {
    const uid = normalizeUid(uidValue);
    const username = normalizeName(usernameValue);
    if (!uid || !username) return "";
    values.set(uid, { username, expiresAt: now() + ttlMs });
    notify(uid, username);
    return username;
  };

  const peek = (uidValue) => {
    const uid = normalizeUid(uidValue);
    const cached = values.get(uid);
    if (!cached) return "";
    if (cached.expiresAt <= now()) {
      values.delete(uid);
      return "";
    }
    return cached.username;
  };

  const resolve = async (uidValue, { force = false } = {}) => {
    const uid = normalizeUid(uidValue);
    if (!uid) return "";
    if (!force) {
      const cached = peek(uid);
      if (cached) return cached;
    }
    if (inFlight.has(uid)) return inFlight.get(uid);

    const request = Promise.resolve(loadUsername(uid))
      .then((username) => seed(uid, username))
      .catch(() => "")
      .finally(() => inFlight.delete(uid));
    inFlight.set(uid, request);
    return request;
  };

  const prefetch = async (uids = []) => {
    const uniqueUids = [...new Set((uids || []).map(normalizeUid).filter(Boolean))];
    const pairs = await Promise.all(uniqueUids.map(async (uid) => [uid, await resolve(uid)]));
    return Object.fromEntries(pairs.filter(([, username]) => username));
  };

  const seedMany = (namesByUid = {}) => {
    Object.entries(namesByUid || {}).forEach(([uid, username]) => seed(uid, username));
  };

  const subscribe = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };

  return { peek, prefetch, resolve, seed, seedMany, subscribe };
}

async function loadPublicUsername(uid) {
  const snapshot = await getDoc(doc(db, "publicUsers", uid));
  if (!snapshot.exists()) return "";
  const data = snapshot.data() || {};
  return data.username || data.userName || data.displayName || data.name || "";
}

export const usernameCache = createUsernameCache({ loadUsername: loadPublicUsername });
export const getCachedUsername = (uid) => usernameCache.peek(uid);
export const prefetchUsernames = (uids) => usernameCache.prefetch(uids);
export const resolveUsername = (uid, options) => usernameCache.resolve(uid, options);
export const seedUsername = (uid, username) => usernameCache.seed(uid, username);
export const seedUsernames = (namesByUid) => usernameCache.seedMany(namesByUid);
export const subscribeUsernameCache = (listener) => usernameCache.subscribe(listener);
